import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const initial = { name: "", email: "", subject: "", message: "" };

const TYPE_PRESETS = {
  "media-kit": {
    subject: "Media Kit Request — Luxury Partnership",
    message:
      "I'd like to receive the WealthFirst Inc. Private Intelligence newsletter media kit. " +
      "Please share the audience profile, sponsor categories, and partnership inquiry next steps.",
  },
  "partnership": {
    subject: "Partnership Inquiry",
    message: "I represent a luxury brand interested in partnering with WealthFirst Inc.",
  },
};

export default function Contact() {
  const [params] = useSearchParams();
  const presetKey = params.get("type");
  const preset = presetKey ? TYPE_PRESETS[presetKey] : null;
  const [form, setForm] = useState(() =>
    preset ? { ...initial, subject: preset.subject, message: preset.message } : initial
  );
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Update if the URL changes mid-session
  useEffect(() => {
    if (preset) {
      setForm((f) => ({ ...f, subject: preset.subject, message: preset.message }));
    }
  }, [presetKey]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-done" className="min-h-[80vh] flex items-center justify-center px-6 pt-24">
        <div className="max-w-xl text-center reveal">
          <CheckCircle2 size={48} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Received</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6">Thank you.</h1>
          <p className="text-lg" style={{ color: "var(--ink-muted)" }}>
            The editorial desk has your note. We will respond personally.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="max-w-[900px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-12 reveal">
        <p className="eyebrow mb-4">Contact</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6 leading-[0.95]">
          Write to the <em style={{ color: "var(--brass)" }}>desk</em>.
        </h1>
        <p className="text-base max-w-2xl" style={{ color: "var(--ink-muted)" }}>
          For general inquiries. For partnerships, press, or guest nominations, please use the{" "}
          <a href="/partners" className="brass-link">Partners</a> intake.
        </p>
      </div>

      <hr className="hairline mb-10" />

      <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow block mb-2">Your Name *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-line" data-testid="contact-name" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" data-testid="contact-email" />
          </div>
        </div>
        <div>
          <label className="eyebrow block mb-2">Subject</label>
          <input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="input-line" data-testid="contact-subject" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Message *</label>
          <textarea required minLength={5} rows={7} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-transparent p-3 text-base outline-none" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }} data-testid="contact-message" />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-brass" data-testid="contact-submit">
            {busy ? "Sending…" : "Send Message"}
          </button>
        </div>
      </form>
    </div>
  );
}
